import type { PhoneConfirmationResult } from "@infrastructure/interfaces/database.interface";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

/** Steps in the phone authentication flow */
export type PhoneAuthStep = "phone" | "code";

/** Default cooldown duration in seconds before allowing SMS resend */
const DEFAULT_RESEND_COOLDOWN_SECONDS = 5;

/**
 * Phone authentication verification state
 */
interface PhoneAuthState {
  /** Current step in the auth flow */
  step: PhoneAuthStep;
  /** Phone number being verified (E.164 format) */
  phoneNumber: string | null;
  /** Confirmation result from Firebase for code verification */
  confirmationResult: PhoneConfirmationResult | null;
  /** Seconds remaining before user can request another SMS */
  resendCooldown: number;
  /** Whether SMS is currently being sent */
  isSendingCode: boolean;
  /** Whether verification code is being confirmed */
  isVerifyingCode: boolean;
  /** Event ID captured from deep link before auth completes */
  pendingEventId: string | null;
}

/**
 * Phone authentication actions
 */
interface PhoneAuthActions {
  /** Set the phone number and move to code entry step */
  setPhoneNumber: (phoneNumber: string) => void;
  /** Store the confirmation result after SMS is sent */
  setConfirmationResult: (result: PhoneConfirmationResult) => void;
  /** Move to the code entry step */
  goToCodeStep: () => void;
  /** Go back to phone number entry step */
  goToPhoneStep: () => void;
  /** Start the resend cooldown timer */
  startResendCooldown: (seconds?: number) => void;
  /** Decrement the cooldown timer by 1 second */
  tickCooldown: () => void;
  /** Set the sending code loading state */
  setIsSendingCode: (isSending: boolean) => void;
  /** Set the verifying code loading state */
  setIsVerifyingCode: (isVerifying: boolean) => void;
  /** Reset all phone auth state */
  reset: () => void;
  /** Store event ID from deep link */
  setPendingEventId: (eventId: string | null) => void;
  /** Clear pending event ID after applying context */
  clearPendingEventId: () => void;
}

/**
 * Combined phone authentication store type
 */
export type PhoneAuthStore = PhoneAuthState & PhoneAuthActions;

/**
 * Initial phone authentication state
 */
const initialState: PhoneAuthState = {
  step: "phone",
  phoneNumber: null,
  confirmationResult: null,
  resendCooldown: 0,
  isSendingCode: false,
  isVerifyingCode: false,
  pendingEventId: null,
};

/**
 * Zustand store for phone authentication verification flow.
 *
 * Manages the two-step phone auth process:
 * 1. Phone number entry and SMS sending
 * 2. Verification code entry and confirmation
 *
 * Also handles resend cooldown timer to prevent SMS spam.
 *
 * @example
 * ```typescript
 * const { step, phoneNumber, setPhoneNumber, goToCodeStep } = usePhoneAuthStore();
 *
 * // After sending SMS
 * setPhoneNumber('+61412345678');
 * setConfirmationResult(result);
 * goToCodeStep();
 * startResendCooldown();
 * ```
 */
export const usePhoneAuthStore = create<PhoneAuthStore>()(
  devtools(
    (set) => ({
      ...initialState,

      setPhoneNumber: (phoneNumber) => set({ phoneNumber }, false, "phoneAuth/setPhoneNumber"),

      setConfirmationResult: (confirmationResult) =>
        set({ confirmationResult }, false, "phoneAuth/setConfirmationResult"),

      goToCodeStep: () => set({ step: "code" }, false, "phoneAuth/goToCodeStep"),

      goToPhoneStep: () =>
        set(
          {
            step: "phone",
            confirmationResult: null,
            resendCooldown: 0,
          },
          false,
          "phoneAuth/goToPhoneStep"
        ),

      startResendCooldown: (seconds = DEFAULT_RESEND_COOLDOWN_SECONDS) =>
        set({ resendCooldown: seconds }, false, "phoneAuth/startResendCooldown"),

      tickCooldown: () =>
        set(
          (state) => ({
            resendCooldown: Math.max(0, state.resendCooldown - 1),
          }),
          false,
          "phoneAuth/tickCooldown"
        ),

      setIsSendingCode: (isSendingCode) => set({ isSendingCode }, false, "phoneAuth/setIsSendingCode"),

      setIsVerifyingCode: (isVerifyingCode) => set({ isVerifyingCode }, false, "phoneAuth/setIsVerifyingCode"),

      reset: () =>
        set(
          (state) => ({
            ...initialState,
            pendingEventId: state.pendingEventId,
          }),
          false,
          "phoneAuth/reset"
        ),

      setPendingEventId: (pendingEventId) => set({ pendingEventId }, false, "phoneAuth/setPendingEventId"),

      clearPendingEventId: () => set({ pendingEventId: null }, false, "phoneAuth/clearPendingEventId"),
    }),
    {
      name: "fitties-phone-auth-store",
      enabled: import.meta.env.DEV,
    }
  )
);
